import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHero } from "@/components/site/PageHero";
import { Checkbox } from "@/components/ui/checkbox";
import { FormSection, SelectField, TextAreaField, TextField } from "@/components/common/Forms";
import { FileUpload } from "@/components/common/FileUpload";
import { SuccessPanel } from "@/components/common/SuccessPanel";
import { SectionHeading } from "@/components/common/Reveal";
import { AMC_SYSTEMS, AMC_TIERS, IMAGES } from "@/data/site";

const Tiers = () => (
  <section className="container-x py-20" data-testid="amc-tiers">
    <SectionHeading eyebrow="Coverage tiers" title="Maintenance that scales from one building to a national footprint." text="Every AMC is scoped after a site survey. Tiers indicate coverage — pricing depends on systems, sites and frequency." />
    <div className="mt-12 grid gap-6 md:grid-cols-3">
      {AMC_TIERS.map((t) => (
        <div key={t.name} className={`rounded-xl border p-8 ${t.featured ? "border-[#123634] bg-[#123634] text-white shadow-xl" : "border-slate-200 bg-white"}`} data-testid={`amc-tier-${t.name.toLowerCase()}`}>
          <p className={`mono text-[11px] uppercase tracking-[0.18em] ${t.featured ? "text-[#E8927F]" : "text-[#C20500]"}`}>{t.tag}</p>
          <h3 className={`mt-3 text-2xl font-bold ${t.featured ? "text-white" : "text-slate-900"}`}>{t.name}</h3>
          <ul className="mt-6 space-y-3">
            {t.features.map((f) => (
              <li key={f} className={`flex items-start gap-2.5 text-sm ${t.featured ? "text-slate-200" : "text-slate-700"}`}><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#E0674F]" />{f}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
);

export default function Amc() {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", city: "", property_type: "", sites_count: "", area_sqft: "", frequency: "", message: "" });
  const [systems, setSystems] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (s) => setSystems((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", { ...f, kind: "amc", systems, attachment_ids: files.map((x) => x.id) });
      setDone(data.reference);
      toast.success("AMC enquiry submitted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="amc-page">
      <PageHero eyebrow="Annual Maintenance Contracts" title="Always inspected. Always refilled. Always audit-ready." text="One accountable AMC for extinguishers, detection, sprinklers, hydrants and suppression — with scheduled visits, service logs and priority breakdown response." image={IMAGES.inspection} />
      <Tiers />
      <section className="bg-[#F4F6F2] py-20" id="amc-form">
        <div className="container-x grid gap-10 lg:grid-cols-3">
          <div>
            <SectionHeading eyebrow="AMC enquiry" title="Get a site-specific AMC proposal." />
            <div className="card-base mt-8 p-6">
              <p className="font-semibold text-slate-900">Already a registered vendor?</p>
              <p className="mt-1 text-sm text-slate-600">Raise AMC renewals and scope changes directly from your portal and track status.</p>
              <Link to="/login" className="btn-outline mt-4" data-testid="amc-portal-login-link"><LogIn className="h-4 w-4" /> Portal login</Link>
            </div>
          </div>
          <div className="lg:col-span-2">
            {done ? (
              <SuccessPanel title="AMC enquiry received" text="Our maintenance team will contact you to schedule a site survey." reference={done} actions={[{ to: "/", label: "Back to home" }]} testid="amc-success" />
            ) : (
              <form onSubmit={submit} className="card-base space-y-8 p-6 sm:p-10" data-testid="amc-enquiry-form">
                <FormSection step="01" title="Contact">
                  <TextField id="amc-name" label="Full name" value={f.name} onChange={set("name")} required />
                  <TextField id="amc-company" label="Company" value={f.company} onChange={set("company")} required />
                  <TextField id="amc-email" type="email" label="Work email" value={f.email} onChange={set("email")} required />
                  <TextField id="amc-phone" label="Phone" value={f.phone} onChange={set("phone")} required />
                </FormSection>
                <FormSection step="02" title="Site profile">
                  <SelectField id="amc-property" label="Property type" value={f.property_type} onChange={set("property_type")} options={["Office / IT park", "Factory / plant", "Hospital", "Institution", "Mall / hotel", "Warehouse", "Residential", "Government / public"]} />
                  <TextField id="amc-city" label="City / region" value={f.city} onChange={set("city")} />
                  <TextField id="amc-sites" label="Number of sites" value={f.sites_count} onChange={set("sites_count")} />
                  <TextField id="amc-area" label="Approx. built-up area (sq ft)" value={f.area_sqft} onChange={set("area_sqft")} />
                  <SelectField id="amc-frequency" label="Preferred visit frequency" value={f.frequency} onChange={set("frequency")} options={["Quarterly", "Half-yearly", "Annual", "Not sure"]} />
                  <div className="col-span-full">
                    <p className="text-[13px] font-medium text-slate-700">Systems to cover</p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-3">
                      {AMC_SYSTEMS.map((s) => (
                        <label key={s} className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm hover:border-slate-400">
                          <Checkbox checked={systems.includes(s)} onCheckedChange={() => toggle(s)} data-testid={`amc-system-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} /> {s}
                        </label>
                      ))}
                    </div>
                  </div>
                </FormSection>
                <FormSection step="03" title="Notes & documents">
                  <TextAreaField id="amc-message" label="Current setup / existing AMC details" value={f.message} onChange={set("message")} className="col-span-full" />
                  <div className="col-span-full"><FileUpload id="amc-files" files={files} onChange={setFiles} max={3} label="Existing service reports or asset list (optional)" /></div>
                </FormSection>
                <button type="submit" disabled={busy} className="btn-primary" data-testid="amc-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Request AMC proposal</button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
